import React, { useState, useEffect } from 'react';
import { User, Phone, Lock, Car, Eye, EyeOff, Plus, Trash2, Save, UserCircle } from 'lucide-react';
import { authService } from '../services/authService';
import { ToastNotificationComponent } from '../components/ToastNotification';
import { HomeScreenSelector } from '../components/HomeScreenSelector';
import { useTranslation } from 'react-i18next';
import { 
  ProfileResponse, 
  UpdateProfileRequest, 
  AddLicensePlateRequest, 
  RemoveLicensePlateRequest, 
  ChangePasswordRequest 
} from '../types';

interface ValidationErrors {
  fullName?: string;
  licensePlate?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
  general?: string;
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form states
  const [fullName, setFullName] = useState('');
  const [newLicensePlate, setNewLicensePlate] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeSection, setActiveSection] = useState<'profile' | 'plates' | 'password'>('profile');
  
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const profileData = await authService.getProfile();
      setProfile(profileData);
      setFullName(profileData.fullName);
    } catch (error: any) {
      console.error('Failed to load profile:', error);
      showToast(t('profile.messages.loadProfileFailed'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const validateFullName = (name: string): string | undefined => {
    if (!name.trim()) {
      return t('profile.personalInfo.errors.fullNameRequired');
    }
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return t('profile.personalInfo.errors.fullNameTwoWords');
    }
    if (nameParts.some(part => part.length < 2)) {
      return t('profile.personalInfo.errors.fullNameMinLength');
    }
    return undefined;
  };

  const validateLicensePlate = (plate: string): string | undefined => {
    if (!plate.trim()) {
      return t('profile.licensePlates.errors.licensePlateRequired');
    }
    const plateRegex = /^\d{3}-\d{2}-\d{3}$/;
    if (!plateRegex.test(plate.trim())) {
      return t('profile.licensePlates.errors.invalidFormat');
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return t('profile.password.errors.newPasswordRequired');
    }
    if (password.length < 8) {
      return t('profile.password.errors.passwordTooShort');
    }
    return undefined;
  };

  const handleLicensePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Remove all non-digits
    let cleaned = value.replace(/\D/g, '');
    
    // Limit to 8 digits max
    if (cleaned.length > 8) {
      cleaned = cleaned.substring(0, 8);
    }
    
    // Apply formatting: 123-45-678
    let formatted = cleaned;
    if (cleaned.length >= 6) {
      formatted = cleaned.substring(0, 3) + '-' + cleaned.substring(3, 5) + '-' + cleaned.substring(5);
    } else if (cleaned.length >= 3) {
      formatted = cleaned.substring(0, 3) + '-' + cleaned.substring(3);
    }
    
    setNewLicensePlate(formatted);
    
    // Clear error when user starts typing
    if (errors.licensePlate) {
      setErrors(prev => ({ ...prev, licensePlate: undefined }));
    }
  };

  const handleUpdateProfile = async () => {
    const nameError = validateFullName(fullName);
    if (nameError) {
      setErrors({ fullName: nameError });
      showToast(nameError, 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      
      const request: UpdateProfileRequest = {
        fullName: fullName.trim()
      };
      
      await authService.updateProfile(request);
      await loadProfile(); // Reload to get updated data
      showToast(t('profile.messages.profileUpdated'), 'success');
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      const errorMessage = error.response?.data?.error || t('profile.messages.updateFailed');
      setErrors({ general: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddLicensePlate = async () => {
    const plateError = validateLicensePlate(newLicensePlate);
    if (plateError) {
      setErrors({ licensePlate: plateError });
      showToast(plateError, 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      
      const request: AddLicensePlateRequest = {
        licensePlate: newLicensePlate.trim()
      };
      
      await authService.addLicensePlate(request);
      await loadProfile(); // Reload to get updated data
      setNewLicensePlate('');
      showToast(t('profile.messages.licensePlateAdded'), 'success');
    } catch (error: any) {
      console.error('Failed to add license plate:', error);
      const errorMessage = error.response?.data?.error || t('profile.messages.addPlateFailed');
      setErrors({ general: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveLicensePlate = async (licensePlate: string) => {
    if (licensePlate === profile?.primaryPlate.value) {
      showToast(t('profile.licensePlates.cannotRemovePrimary'), 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const request: RemoveLicensePlateRequest = {
        licensePlate
      };
      
      await authService.removeLicensePlate(request);
      await loadProfile(); // Reload to get updated data
      showToast(t('profile.messages.licensePlateRemoved'), 'success');
    } catch (error: any) {
      console.error('Failed to remove license plate:', error);
      const errorMessage = error.response?.data?.error || t('profile.messages.removePlateFailed');
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    const currentPasswordError = !currentPassword ? t('profile.password.errors.currentPasswordRequired') : 
                                 currentPassword.length < 8 ? t('profile.password.errors.passwordTooShort') : undefined;
    const newPasswordError = validatePassword(newPassword);
    
    const newErrors: ValidationErrors = {};
    if (currentPasswordError) newErrors.currentPassword = currentPasswordError;
    if (newPasswordError) newErrors.newPassword = newPasswordError;
    
    if (!confirmPassword) {
      newErrors.confirmPassword = t('profile.password.errors.confirmPasswordRequired');
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('profile.password.errors.passwordMismatch');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast(t('profile.messages.fixErrors'), 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      
      const request: ChangePasswordRequest = {
        currentPassword,
        newPassword
      };
      
      await authService.changePassword(request);
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      showToast(t('profile.messages.passwordChanged'), 'success');
    } catch (error: any) {
      console.error('Failed to change password:', error);
      const errorMessage = error.response?.data?.error || t('profile.messages.changePasswordFailed');
      setErrors({ general: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <UserCircle size={48} style={{ color: '#3b82f6', marginBottom: '1rem' }} />
          <p>{t('profile.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '1rem',
      paddingTop: '80px' // Account for header
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          <div style={{
            backgroundColor: '#3b82f6',
            borderRadius: '50%',
            width: '80px',
            height: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            color: 'white'
          }}>
            <UserCircle size={40} />
          </div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: 'bold',
            margin: '0 0 0.5rem 0',
            color: '#1f2937'
          }}>{t('profile.title')}</h1>
          <p style={{
            color: '#6b7280',
            margin: 0
          }}>{t('profile.subtitle')}</p>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          marginBottom: '1rem',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e5e7eb'
          }}>
            {[
              { key: 'profile', label: t('profile.tabs.personalInfo'), icon: User },
              { key: 'plates', label: t('profile.tabs.licensePlates'), icon: Car },
              ...(profile?.hasPassword ? [{ key: 'password', label: t('profile.tabs.password'), icon: Lock }] : [])
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveSection(key as any)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  border: 'none',
                  backgroundColor: activeSection === key ? '#3b82f6' : 'transparent',
                  color: activeSection === key ? 'white' : '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          padding: '2rem'
        }}>
          {/* Personal Info Section */}
          {activeSection === 'profile' && (
            <div>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
                }}>
                <User size={20} />
                {t('profile.personalInfo.title')}
              </h2>              {/* Phone Number (Read-only) */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  <Phone size={18} />
                  {t('profile.personalInfo.phoneNumber')}
                </label>
                <input
                  type="text"
                  value={profile?.phoneE164.value || ''}
                  disabled
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: '#f9fafb',
                    color: '#6b7280'
                  }}
                />
                <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                  {t('profile.personalInfo.phoneNumberHelp')}
                </small>
              </div>

              {/* Full Name */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="fullName" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  <User size={18} />
                  {t('profile.personalInfo.fullName')}
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (errors.fullName) {
                      setErrors(prev => ({ ...prev, fullName: undefined }));
                    }
                  }}
                  placeholder={t('profile.personalInfo.fullNamePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: errors.fullName ? '2px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    backgroundColor: isSubmitting ? '#f9fafb' : 'white'
                  }}
                  disabled={isSubmitting}
                />
                {errors.fullName && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.fullName}</span>}
              </div>

              {/* Home Screen Preference */}
              <div style={{ marginBottom: '2rem' }}>
                <HomeScreenSelector />
              </div>

              {/* Account Type */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  {t('profile.personalInfo.accountType')}
                </label>
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  color: '#374151'
                }}>
                  {profile?.hasTelegram ? t('profile.personalInfo.telegramAccount') : t('profile.personalInfo.passwordAccount')}
                </div>
              </div>

              {/* Update Button */}
              <button
                onClick={handleUpdateProfile}
                disabled={isSubmitting || fullName === profile?.fullName}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: (isSubmitting || fullName === profile?.fullName) ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: (isSubmitting || fullName === profile?.fullName) ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                <Save size={18} />
                {isSubmitting ? t('profile.personalInfo.updating') : t('profile.personalInfo.updateButton')}
              </button>
            </div>
          )}

          {/* License Plates Section */}
          {activeSection === 'plates' && (
            <div>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
                }}>
                <Car size={20} />
                {t('profile.licensePlates.title')}
              </h2>              {/* Current Plates */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '500',
                  marginBottom: '1rem',
                  color: '#374151'
                }}>
                  {t('profile.licensePlates.yourPlates')}
                </h3>
                
                {profile?.allPlates.map((plate) => (
                  <div
                    key={plate.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      backgroundColor: plate.value === profile.primaryPlate.value ? '#f0f9ff' : 'white'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Car size={18} color="#374151" />
                      <span style={{ fontWeight: '500' }}>{plate.value}</span>
                      {plate.value === profile.primaryPlate.value && (
                        <span style={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          {t('profile.licensePlates.primary')}
                        </span>
                      )}
                    </div>
                    
                    {plate.value !== profile.primaryPlate.value && (
                      <button
                        onClick={() => handleRemoveLicensePlate(plate.value)}
                        disabled={isSubmitting}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#ef4444',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add New Plate */}
              <div>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '500',
                  marginBottom: '1rem',
                  color: '#374151'
                }}>
                  {t('profile.licensePlates.addNew')}
                </h3>
                
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={newLicensePlate}
                      onChange={handleLicensePlateChange}
                      placeholder={t('profile.licensePlates.addNewPlaceholder')}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: errors.licensePlate ? '2px solid #ef4444' : '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      disabled={isSubmitting}
                    />
                    {errors.licensePlate && (
                      <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.licensePlate}</span>
                    )}
                  </div>
                  
                  <button
                    onClick={handleAddLicensePlate}
                    disabled={isSubmitting || !newLicensePlate.trim()}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: (isSubmitting || !newLicensePlate.trim()) ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (isSubmitting || !newLicensePlate.trim()) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Plus size={18} />
                  </button>
                </div>
                
                <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                  {t('profile.licensePlates.formatHelp')}
                </small>
              </div>
            </div>
          )}

          {/* Password Section (only for non-Telegram users) */}
          {activeSection === 'password' && profile?.hasPassword && (
            <div>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
                }}>
                <Lock size={20} />
                {t('profile.password.title')}
              </h2>              {/* Current Password */}
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="currentPassword" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  <Lock size={18} />
                  {t('profile.password.currentPassword')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      if (errors.currentPassword) {
                        setErrors(prev => ({ ...prev, currentPassword: undefined }));
                      }
                    }}
                    placeholder={t('profile.password.currentPasswordPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.75rem 3rem 0.75rem 0.75rem',
                      border: errors.currentPassword ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      backgroundColor: isSubmitting ? '#f9fafb' : 'white'
                    }}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    disabled={isSubmitting}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.currentPassword && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.currentPassword}</span>}
              </div>

              {/* New Password */}
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="newPassword" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  <Lock size={18} />
                  {t('profile.password.newPassword')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (errors.newPassword) {
                        setErrors(prev => ({ ...prev, newPassword: undefined }));
                      }
                    }}
                    placeholder={t('profile.password.newPasswordPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.75rem 3rem 0.75rem 0.75rem',
                      border: errors.newPassword ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      backgroundColor: isSubmitting ? '#f9fafb' : 'white'
                    }}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={isSubmitting}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.newPassword && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.newPassword}</span>}
              </div>

              {/* Confirm New Password */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="confirmPassword" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  <Lock size={18} />
                  {t('profile.password.confirmPassword')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                      }
                    }}
                    placeholder={t('profile.password.confirmPasswordPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.75rem 3rem 0.75rem 0.75rem',
                      border: errors.confirmPassword ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      backgroundColor: isSubmitting ? '#f9fafb' : 'white'
                    }}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isSubmitting}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.confirmPassword}</span>}
              </div>

              {/* Change Password Button */}
              <button
                onClick={handleChangePassword}
                disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: (isSubmitting || !currentPassword || !newPassword || !confirmPassword) ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: (isSubmitting || !currentPassword || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                <Lock size={18} />
                {isSubmitting ? t('profile.password.changing') : t('profile.password.changeButton')}
              </button>
            </div>
          )}

          {/* General Error */}
          {errors.general && (
            <div style={{ 
              color: '#ef4444', 
              fontSize: '0.875rem',
              textAlign: 'center',
              padding: '0.75rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginTop: '1rem'
            }}>
              {errors.general}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast.isVisible && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 1000
        }}>
          <ToastNotificationComponent
            notification={{
              id: 'profile-toast',
              type: 'General',
              message: toast.message,
              timestamp: new Date(),
              severity: toast.type === 'success' ? 'success' : toast.type === 'error' ? 'error' : 'info'
            }}
            onClose={hideToast}
          />
        </div>
      )}
    </div>
  );
};

export default ProfilePage;